import type { ProviderProfile } from './provider-profiles';
import type { Pitch, Season, Character, ShotSpec, Qa, Role, EpisodeReview } from './index';
export interface ProjectView {
  _id: string;
  title: string;
  idea: string;
  genre: string;
  phase: string;
  pitchVersion: number;
  seasonVersion: number;
  castLocked: boolean;
  visualDirection: string;
  createdAt: number;
  updatedAt: number;
  /** Present on a CLAPTAL Kids series. */
  kids?: { language: string; style: string; category: string; ageMin: number; ageMax: number };
}
export interface EpisodeView {
  _id: string;
  projectId: string;
  number: number;
  title: string;
  synopsis: string;
  cliffhanger: string;
  status: string;
  scriptVersion: number;
  screenplay?: string;
  revision: number;
  narrationAssetId?: string;
  currentMasterId?: string;
  currentCaptionsId?: string;
  acceptedMasterId?: string;
  /** Pipeline 2's story; for CLAPTAL Kids it carries what the episode teaches. */
  story?: { learning?: { point: string; question: string; answers: string[]; correct: number } };
  /** What the screenplay says about itself, and what the script doctor made of the draft. */
  scriptNotes?: {
    hook?: string;
    cliffhanger?: string;
    posterShotKey?: string;
    doctor?: { scores: Record<string, number>; notes: string[] };
    doctorSkipped?: string;
    review?: { scores: Record<string, number>; notes: string[]; passed: boolean };
  };
  /** The review of the finished cut, and the shot it had filmed again, if any. */
  review?: EpisodeReview & {
    remade?: { shotKey: string; restored?: boolean };
  };
}
export interface ShotView {
  _id: string;
  projectId: string;
  episodeId: string;
  key: string;
  spec: ShotSpec;
  status: string;
  currentVersion: number;
  acceptedVersion?: number;
  scriptVersion: number;
}
export interface VersionView {
  _id: string;
  shotId: string;
  version: number;
  spec: ShotSpec;
  referenceAssetId?: string;
  videoAssetId?: string;
  qaReference?: Qa;
  qaVideo?: Qa;
  humanAccepted: boolean;
  bibleVersion?: number;
}
export interface ActorView {
  _id: string;
  name: string;
  kind: 'platform' | 'custom';
  projectId?: string;
  description: string;
  voice: string;
  identityAssetId?: string;
  locked: boolean;
}
export interface CharacterView {
  _id: string;
  projectId: string;
  key: string;
  data: Pitch['characters'][number];
}
export interface CommandView {
  _id: string;
  projectId: string;
  episodeId?: string;
  kind: string;
  status: string;
  createdAt: number;
  error?: string;
  pauseReason?: string;
  resumeAt?: number;
}
export interface JobView {
  _id: string;
  commandId: string;
  kind: string;
  status: string;
  model: string;
  error?: string;
  reservation: number;
  output?: { costMicros: number; costBasis: string };
}
export interface HomeView {
  executorTransport?: 'local' | 'trigger';
  allowanceRequired?: boolean;
  trialRemaining?: number;
  providerProfiles?: ProviderProfile[];
  allowance?: { cap: number; spent: number; reserved: number; active: boolean } | null;
  library?: ActorView[];
  metrics?: {
    completedOperations: number;
    averageLatencyMs: number;
    retries: number;
    continuityFailures: number;
    viewerCount: number;
    wantsNext: number;
    averageDialogue: number | null;
    confirmedCost: number;
  };
  access: { role: Role; email: string; name: string };
  projects: ProjectView[];
  budget: {
    cap: number;
    spent: number;
    reserved: number;
    suspended: boolean;
    storageBytes: number;
    trial?: { projectId: string; workerId: string; baselineSpent: number; cap: number };
  };
  workerOnline: boolean;
  commands: CommandView[];
}
/**
 * A bible row is a discriminated union over `kind`. The Convex schema stores `data` as
 * `v.any()` because the shape varies per kind, but every writer parses it through the
 * matching Zod schema in `./index` first, so the union below is what actually lands in
 * the table. Keeping it typed here lets `b.kind === 'pitch'` narrow `b.data` instead of
 * each reader casting.
 */
export type BibleView = { _id: string; version: number } & (
  | { kind: 'pitch'; data: Pitch }
  | { kind: 'story'; data: Season['storyBible'] }
  | { kind: 'visual'; data: Season['visualBible'] }
  | { kind: 'season'; data: Season['episodes'] }
  | { kind: 'cast'; data: CastEntryView[] }
);
export const isBibleKind =
  <K extends BibleView['kind']>(kind: K) =>
  (row: BibleView): row is Extract<BibleView, { kind: K }> =>
    row.kind === kind;
export interface CastEntryView {
  character: Character;
  actorId: string;
  identityAssetId: string;
  voice: string;
}
export interface ProjectDetail {
  managedProviderProfile?: string;
  generationUnavailable?: string;
  trialRemaining?: number;
  project: ProjectView;
  bibles: BibleView[];
  characters: CharacterView[];
  castings: { characterId: string; actorId: string }[];
  actors: ActorView[];
  episodes: EpisodeView[];
  canon: {
    subject: string;
    predicate: string;
    value: string;
    validFrom: number;
    knownFrom: number;
  }[];
  shots: ShotView[];
  versions: VersionView[];
  commands: CommandView[];
  jobs: JobView[];
  assets: {
    _id: string;
    key: string;
    mime: string;
    deleted?: boolean;
    retention?: string;
    expiresAt?: number;
  }[];
  /** Places the current season already has a passed set plate for. */
  plates?: string[];
}
